/**
 * Raw design tokens — the Outright brand palette and its warm neutral ramps.
 * Nothing in the application reads these directly; semanticTokens.js maps them
 * onto roles, and components consume the roles.
 *
 * The four logo colours are amber/orange, coral, teal and blue. Orange is the
 * dashboard's dominant personality; the other three appear only where they
 * carry meaning.
 */
export const tokens = {
  colors: {
    /**
     * Brand orange — the amber of the client site's hiring bar, extended into a
     * full ramp. 400 is the signature fill and carries dark text (7.6:1), the
     * same relationship the public site uses. 600–800 are the ochre/burnt end,
     * for amber text on light surfaces.
     */
    orange: {
      50: { value: '#FEF6EC' },
      100: { value: '#FCE7CD' },
      200: { value: '#F8CE9C' },
      300: { value: '#F3AE60' },
      400: { value: '#EC8B1E' },
      500: { value: '#D2760F' },
      600: { value: '#AA5D0D' },
      700: { value: '#88490C' },
      800: { value: '#67380B' },
      900: { value: '#452508' },
      950: { value: '#2A1605' },
    },

    /** Brand coral — the client site's call-to-action red. */
    coral: {
      50: { value: '#FEF1EE' },
      100: { value: '#FCDCD5' },
      200: { value: '#F8B9AB' },
      300: { value: '#F39181' },
      400: { value: '#ED6C57' },
      500: { value: '#DE4E36' },
      600: { value: '#BC3D28' },
      700: { value: '#96311F' },
      800: { value: '#6F2517' },
      900: { value: '#4A1810' },
      950: { value: '#2B0E09' },
    },

    /** Brand teal — the logo's green-teal segment. Success and positive trend. */
    teal: {
      50: { value: '#E9F7F4' },
      100: { value: '#C8EBE4' },
      200: { value: '#97D9CD' },
      300: { value: '#61C2B2' },
      400: { value: '#34A798' },
      500: { value: '#1B8C7E' },
      600: { value: '#147065' },
      700: { value: '#115A52' },
      800: { value: '#0E453F' },
      900: { value: '#0A2E2A' },
      950: { value: '#061917' },
    },

    /** Brand blue — the logo's blue segment. Information and links. */
    blue: {
      50: { value: '#EDF3FE' },
      100: { value: '#D5E2FC' },
      200: { value: '#ABC5F8' },
      300: { value: '#7BA3F1' },
      400: { value: '#5081E4' },
      500: { value: '#3563CE' },
      600: { value: '#284EA8' },
      700: { value: '#223F85' },
      800: { value: '#1D3468' },
      900: { value: '#172745' },
      950: { value: '#0D1526' },
    },

    /**
     * Warm neutrals: cream and sand through to espresso charcoal. Every surface
     * and every line of text comes from this ramp, which is what makes the
     * product feel warm without painting anything orange.
     */
    warm: {
      0: { value: '#FFFFFF' }, // raised surfaces: menus, dialogs, inputs
      25: { value: '#FEFDFB' },
      50: { value: '#FCFBF8' }, // card — warm white, not cream
      75: { value: '#F6F4EF' }, // page — refined ivory
      100: { value: '#EFEDE6' }, // sidebar — light stone
      150: { value: '#E8E5DC' }, // subtle panel, neutral hover
      200: { value: '#DAD6CB' }, // border default — taupe, off the yellow axis
      250: { value: '#C9C4B7' }, // input border
      300: { value: '#B3ADA0' }, // border strong
      400: { value: '#8E8879' }, // disabled text, muted icon
      450: { value: '#A79F91' }, // muted text on dark surfaces
      500: { value: '#676257' }, // muted text on light surfaces (4.8:1 on subtle panels)
      600: { value: '#514D44' }, // secondary text
      700: { value: '#3E3A33' },
      750: { value: '#332F29' },
      800: { value: '#292520' },
      850: { value: '#221F1A' }, // raised dark surface
      900: { value: '#1C1915' }, // dark card
      925: { value: '#171410' }, // dark topbar
      950: { value: '#141210' }, // dark page
      975: { value: '#100E0C' }, // dark sidebar
      1000: { value: '#201E1A' }, // primary text — near-black, warm
    },

    /** Overlay scrims, kept here so no component invents an alpha value. */
    scrim: {
      light: { value: 'rgba(32, 30, 26, 0.40)' },
      dark: { value: 'rgba(6, 5, 4, 0.66)' },
      /**
       * Bottom-to-top warm charcoal fade for the authentication image panel.
       * Carries overlay text on any crop without tinting the photograph orange.
       */
      panelFade: {
        value:
          'linear-gradient(to top, rgba(20,18,16,0.92) 0%, rgba(20,18,16,0.72) 38%, rgba(20,18,16,0.35) 70%, rgba(20,18,16,0.20) 100%)',
      },
    },
  },

  fonts: {
    /**
     * Plus Jakarta Sans: rounded-geometric letterforms that match the client
     * site's headings, with a wide weight range and real tabular figures.
     */
    heading: {
      value: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    body: {
      value: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
  },

  /** Compact dashboard scale. 28px only on the authentication screen. */
  fontSizes: {
    '2xs': { value: '0.6875rem' }, // 11px — badge text, captions
    xs: { value: '0.75rem' }, // 12px — helper text, table headings
    sm: { value: '0.8125rem' }, // 13px — table cells, body small
    md: { value: '0.875rem' }, // 14px — body, controls, nav
    lg: { value: '0.9375rem' }, // 15px — card titles
    xl: { value: '1rem' }, // 16px — section titles
    '2xl': { value: '1.25rem' }, // 20px — page titles
    '3xl': { value: '1.5rem' }, // 24px — metric values
    '4xl': { value: '1.75rem' }, // 28px — auth heading
  },

  fontWeights: {
    normal: { value: '400' },
    medium: { value: '500' },
    semibold: { value: '600' },
    bold: { value: '700' },
  },

  lineHeights: {
    none: { value: '1' },
    tight: { value: '1.2' },
    snug: { value: '1.4' },
    normal: { value: '1.55' },
  },

  /** Headings tighten; body stays at zero. No loose marketing tracking. */
  letterSpacings: {
    tighter: { value: '-0.022em' },
    tight: { value: '-0.014em' },
    normal: { value: '0' },
  },

  /** 4px base with 2px half-steps up to 4. */
  spacing: {
    px: { value: '1px' },
    0.5: { value: '0.125rem' },
    1: { value: '0.25rem' },
    1.5: { value: '0.375rem' },
    2: { value: '0.5rem' },
    2.5: { value: '0.625rem' },
    3: { value: '0.75rem' },
    3.5: { value: '0.875rem' },
    4: { value: '1rem' },
    5: { value: '1.25rem' },
    6: { value: '1.5rem' },
    8: { value: '2rem' },
    10: { value: '2.5rem' },
    12: { value: '3rem' },
    16: { value: '4rem' },
  },

  /** controls/nav 8 · cards 12 · dialogs 16 · full for avatars, dots, pills. */
  radii: {
    xs: { value: '4px' },
    sm: { value: '6px' },
    md: { value: '8px' },
    lg: { value: '12px' },
    xl: { value: '16px' },
    full: { value: '9999px' },
  },

  /**
   * Four rungs, each with one job: xs resting cards, sm sticky chrome,
   * md dropdowns and popovers, lg dialogs and drawers. Warm-tinted, not grey.
   */
  shadows: {
    // Cards are near-flat: a hairline of depth so they lift off the ivory page
    // without floating. Surface difference and borders do the rest.
    xsLight: { value: '0 1px 1px rgba(48, 40, 28, 0.04)' },
    // Sticky chrome, e.g. the topbar once content scrolls under it.
    stickyLight: { value: '0 1px 0 rgba(48, 40, 28, 0.05), 0 2px 6px rgba(48, 40, 28, 0.04)' },
    smLight: { value: '0 1px 2px rgba(48, 40, 28, 0.06), 0 1px 3px rgba(48, 40, 28, 0.04)' },
    mdLight: { value: '0 4px 12px rgba(48, 40, 28, 0.10), 0 1px 3px rgba(48, 40, 28, 0.06)' },
    lgLight: { value: '0 16px 40px rgba(38, 31, 22, 0.16), 0 2px 8px rgba(38, 31, 22, 0.07)' },
    xsDark: { value: '0 1px 1px rgba(0, 0, 0, 0.30)' },
    stickyDark: { value: '0 1px 0 rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)' },
    smDark: { value: '0 1px 3px rgba(0, 0, 0, 0.42)' },
    mdDark: { value: '0 6px 18px rgba(0, 0, 0, 0.5)' },
    lgDark: { value: '0 20px 48px rgba(0, 0, 0, 0.62)' },
  },

  sizes: {
    /** Shared by buttons, inputs, selects and icon buttons. */
    control: {
      xs: { value: '1.5rem' }, // 24px
      sm: { value: '1.75rem' }, // 28px
      md: { value: '2rem' }, // 32px — default
      lg: { value: '2.25rem' }, // 36px
    },
    /** One icon scale for the whole product. */
    icon: {
      xs: { value: '0.75rem' }, // 12px
      sm: { value: '0.875rem' }, // 14px — buttons, table actions
      md: { value: '1rem' }, // 16px — nav, topbar
      lg: { value: '1.25rem' }, // 20px — empty states
    },
    row: {
      sm: { value: '2.25rem' }, // 36px — dense table row
      md: { value: '2.75rem' }, // 44px — table row
    },
    nav: { value: '2.25rem' }, // 36px — sidebar item
    sidebar: {
      expanded: { value: '15rem' }, // 240px
      collapsed: { value: '3.5rem' }, // 56px
    },
    topbar: { value: '3.5rem' }, // 56px
    container: {
      content: { value: '90rem' }, // 1440px
      form: { value: '40rem' }, // 640px
      prose: { value: '44rem' },
    },
  },

  durations: {
    fast: { value: '120ms' },
    normal: { value: '180ms' },
  },

  easings: {
    standard: { value: 'cubic-bezier(0.2, 0, 0.2, 1)' },
  },

  zIndex: {
    base: { value: '0' },
    sticky: { value: '10' },
    sidebar: { value: '20' },
    topbar: { value: '30' },
    overlay: { value: '40' },
    dialog: { value: '50' },
    toast: { value: '60' },
  },
}

export const breakpoints = {
  sm: '30rem', // 480px
  md: '48rem', // 768px
  lg: '64rem', // 1024px — sidebar appears
  xl: '80rem', // 1280px
  '2xl': '96rem', // 1536px
}
